import { v4 as uuid } from "uuid";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import type { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import type { ProductViewed } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductViewed";
import type { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import { FormatType } from "@/constants/tagular/main";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { TagularAction, TagularCategory } from "@/types/Tagular";
import { setCorrelationId, getTagularSubject } from "@/utils/tagular/helpers";
import { sendEvent } from "@/lib/segment/sender";
import type { ProductCompareData } from "./useProductCompareData";

/** Mirrors ComparisonCtaLocation from hen-ui (not re-exported from the package root). */
type CtaLocation = "top" | "bottom";

export interface CompareProductEventCallbacks {
  /** Fire when the column enters the viewport — hen-ui calls via onProductViewed(id) */
  onViewed: () => void;
  /** Fire when a CTA button inside the column is clicked — fires alongside onCtaClicked */
  onClicked: () => void;
  /**
   * Fire when a CTA button inside the column is clicked — hen-ui calls via onCtaClick(id, cta, location).
   * Also fires onClicked (ProductClicked) alongside the Segment discovery card click event.
   */
  onCtaClicked: (
    cta: { label: string; href: string },
    location: CtaLocation,
  ) => void;
}

/**
 * Builds plain ProductViewed / ProductClicked fire functions for each product
 * in the comparison table, keyed by productUuid.
 *
 * No hooks — just event payload builders using makeEvent directly.
 * hen-ui will call onProductViewed(id) / onProductClicked(id) for the column
 * that came into view or was clicked; we look up the right callback by id.
 *
 * @param stableCorrelationIds  Optional map of productUuid → pre-generated correlationId.
 *   Pass a persistent ref-backed map (see ProductCompare/index.tsx) so the same ID
 *   survives re-renders and is consistent across onClicked → onCtaClicked → PDP.
 *
 * @example
 * // In ProductCompare, once hen-ui ships the props:
 * // onProductViewed={(id) => compareEvents[id]?.onViewed()}
 * // onProductClicked={(id) => compareEvents[id]?.onClicked()}
 */
export function buildCompareProductEvents(
  products: ProductCompareData[],
  location: string,
  stableCorrelationIds?: Record<string, string>,
): Record<string, CompareProductEventCallbacks> {
  const result: Record<string, CompareProductEventCallbacks> = {};

  // On a program page include all table products; on a course page only the first (primary) product.
  const isProgramPage = location === "program-detail-page";
  const conversionProductList = isProgramPage
    ? (products[0]?.memberCourses ?? []).map((c) => ({
        name: c.productName,
        brand: products[0]?.brand ?? products[0]?.partnerName ?? "",
        variant: translateVariant(products[0]?.productType ?? ""),
        category: getTagularSubject(products[0]?.attributes),
        id: c.key,
        externalId: c.activeCourseRunKey,
      }))
    : products.slice(0, 1).map((p) => ({
        name: p.productName,
        brand: p.brand ?? p.partnerName ?? "",
        variant: translateVariant(p.productType ?? ""),
        category: getTagularSubject(p.attributes),
        externalId: p.productRunKey,
        id: p.productKey,
      }));

  products.forEach((product, index) => {
    const productPayload: ProductClicked["product"] = {
      brand: product.brand ?? product.partnerName ?? "",
      category: getTagularSubject(product.attributes),
      formatSubtype: location,
      formatType: FormatType.ProductCard,
      location: "compare-tool",
      name: product.productName,
      position: index + 1,
      productId: product.productUuid,
      variant: translateVariant(product.productType ?? ""),
    };

    const viewCorrelationId = uuid();
    const correlationId = stableCorrelationIds?.[product.productUuid] ?? uuid();

    const fireEvent = makeEvent<
      { product: typeof productPayload; viewCorrelationId: string },
      EVENTS
    >({ product: productPayload, viewCorrelationId });

    result[product.productUuid] = {
      onViewed: () => {
        fireEvent<ProductViewed>(EVENTS.ProductViewed)();
      },
      onClicked: () => {
        setCorrelationId(correlationId);
        fireEvent<ProductClicked>(EVENTS.ProductClicked)({
          actionOutcome: "Click",
          correlationId,
        });
      },
      onCtaClicked: (cta, ctaLocation) => {
        // The first item fires ConversionTracked only — no Segment/ProductClicked.
        // Writing correlationId to the cookie ensures the PDP picks up the same ID.
        if (index === 0) {
          setCorrelationId(correlationId);
          const conversionEvent: ConversionTracked = {
            correlation: { id: correlationId },
            metadata: {
              action: TagularAction.STARTED,
              category: TagularCategory.ENROLLMENT,
              customName: "compare-tool",
              timestamp: new Date().toISOString(),
              productList: conversionProductList,
            },
          };
          const fireConversion = makeEvent<ConversionTracked, EVENTS>(
            conversionEvent,
          );
          fireConversion<ConversionTracked>(EVENTS.ConversionTracked)();
          return;
        }

        // Write to the shared cookie so the PDP's getCorrelationID() picks up
        // this same ID when it fires ConversionTracked on checkout/CYT.
        setCorrelationId(correlationId);

        const isProgram = !product.productType
          ?.toLowerCase()
          .includes("course");
        sendEvent("edx.bi.user.discovery.card.click", {
          card_visibility: "visible",
          category: "navigation",
          hasDeviceParam: true,
          is_product_card_variation: false,
          label: `${product.productName} [${product.productUuid}]`,
          link: cta.href,
          position: index + 1,
          product_category: isProgram ? "program" : "course",
          location: "compare-tool",
          cta_location: ctaLocation,
          source: "edX",
        });
      },
    };
  });

  return result;
}
